import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { type Props as JustificationContainerProps } from '~/components/atom/JustificationContainer';

import TEST_ID from './index.testid';

export type LineTab = {
  key: string;
  label: string;
  dataTestId: string;
  accentedLabel?: string | null;
};
type Props = {
  selectedKey: string | null;
  tabs: Array<LineTab>;
  justification?: JustificationContainerProps['justification'];
  onSelect: (key: string) => void;
};
const LineTabs: React.FCC<Props> = ({
  tabs,
  selectedKey,
  onSelect,
  justification,
}) => (
  <Container
    data-testid={TEST_ID.CONTAINER}
    data-selectedkey={selectedKey}
    $justification={justification}
  >
    {tabs.map(tab => {
      let accentedLabel: ReactElement | null = null;
      if (tab.accentedLabel != null) {
        accentedLabel = (
          <AccentedLabel $selected={tab.key === selectedKey}>
            {tab.accentedLabel}
          </AccentedLabel>
        );
      }

      return (
        <TabHeader
          key={tab.key}
          selected={tab.key === selectedKey}
          onClick={() => {
            if (tab.key !== selectedKey) {
              onSelect(tab.key);
            }
          }}
          data-testid={tab.dataTestId}
        >
          {tab.label}
          {accentedLabel}
        </TabHeader>
      );
    })}
  </Container>
);

type TabHeaderProps = {
  selected: boolean;
};
const TabHeader = styled.div<TabHeaderProps>`
  position: relative;
  cursor: pointer;
  display: inline-block;

  ${({ theme, selected }) => css`
    color: ${selected ? theme.color('secondary') : theme.color('primary')};
    padding: ${theme.space('s')} ${theme.space('s')};
  `};

  ${({ theme, selected }) => {
    if (selected) {
      return css`
        font-weight: ${theme.fw('semiBold')};

        &:after {
          content: '';
          height: ${theme.space('xxxs')};
          width: 100%;
          background-color: ${theme.color('secondary')};
          position: absolute;
          bottom: -1px;
          left: 0;
        }
      `;
    }

    return '';
  }}
`;

const AccentedLabel = styled.div<{ $selected: boolean }>`
  position: absolute;
  top: 0;
  white-space: nowrap;

  ${({ theme, $selected }) => css`
    padding: 4px;
    border-radius: ${theme.getTokens().border.radius.s};
    color: ${$selected
      ? theme.color('secondary')
      : theme.color('tertiary', 'dark')};
    background-color: ${$selected
      ? theme.color('secondary', 'translucent')
      : theme.color('tertiary', 'light')};
    left: calc(100% - ${theme.space('xs')});
  `}
`;

const Container = styled.div<{
  $justification?: JustificationContainerProps['justification'];
}>(
  ({ theme, $justification }) => css`
    width: 100%;
    display: flex;
    justify-content: ${$justification ? $justification : 'center'};

    border-bottom: 1px solid ${theme.color('tertiary')};
    margin-bottom: ${theme.space('base')};
  `,
);

export default LineTabs;
